import classNames from "classnames";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	MouseEvent,
	ReactElement,
	useCallback
} from "react";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import { FontIconName, Icon } from "../Icon/Icon";
import styles from "./Tag.module.less";
import { Highlight, HighlightedText } from "../HighlightedText/HighlightedText";

export type TagColor =
	| "primary"
	| "secondary"
	| "info"
	| "success"
	| "warning"
	| "danger";

export type TagSize =
	| "normal"
	| "small";

export type TagProps = {
	text: string;
	color?: TagColor;
	onClick?(): void;
	onDelete?(): void;
	isDeleteFocusable?: boolean;
	trackingId?: string;
	isDisabled?: boolean;
	shouldBubbleClick?: boolean;
	size?: TagSize;
	highlight?: Highlight | nil;
	icon?: FontIconName;
};

export const Tag = (props: TagProps): ReactElement => {
	const {
		text,
		color = "primary",
		onDelete,
		isDeleteFocusable = true,
		onClick,
		trackingId,
		isDisabled,
		shouldBubbleClick,
		size = "normal",
		highlight,
		icon
	} = props;

	const deleteHandler = (event: MouseEvent): void => {
		if (!isDeleteFocusable || shouldBubbleClick === false) {
			event.preventDefault();
		}

		event.stopPropagation();
		onDelete?.();
	};

	const clickHandler = useCallback(
		(args: MouseEvent) => {
			if (isDisabled || !onClick) {
				return;
			}

			if (shouldBubbleClick === false) {
				args.preventDefault();
				args.stopPropagation();
			}

			onClick?.();
		},
		[
			isDisabled,
			shouldBubbleClick,
			onClick
		]
	);

	const isClickable: boolean = !isDisabled && isNotNil(onClick);

	const renderContent = (): ReactElement | string => {
		return (
			<>
				{icon && (
					<Icon
						value={icon}
						size="small"
					/>
				)}
				<span className={styles.text}>
					{isNotNil(highlight)
						? (
							<HighlightedText
								text={text}
								highlight={highlight}
							/>
						)
						: text
					}
				</span>
			</>
		);
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				colorToStyleMap[color],
				sizeToStyleMap[size],
				{
					[styles.root_clickable]: isClickable,
					[styles.root_disabled]: isDisabled
				}
			)}
		>
			{isClickable
				? (
					<ButtonBase
						trackingId={generateTrackingId(trackingId, "__name")}
						className={styles.content}
						onClick={clickHandler}
					>
						{renderContent()}
					</ButtonBase>
				)
				: (
					<span
						{...setTrackingId(trackingId, "__name")}
						className={styles.content}
					>
						{renderContent()}
					</span>
				)
			}
			{!isDisabled && onDelete && (
				<ButtonBase
					trackingId={generateTrackingId(trackingId, "__close")}
					onClick={deleteHandler}
					className={styles.deleteButton}
					tabIndex={isDeleteFocusable ? undefined : -1}
				>
					<Icon
						value={{
							kind: "font",
							name: "xmark",
						}}
						size="small"
					/>
				</ButtonBase>
			)}
		</div>
	);
};
Tag.displayName = "Tag";

const colorToStyleMap: { [key in TagColor]: string | undefined } = {
	["primary"]: undefined,
	["info"]: styles.root_info,
	["success"]: styles.root_success,
	["warning"]: styles.root_warning,
	["danger"]: styles.root_danger,
	["secondary"]: styles.root_secondary
};

const sizeToStyleMap: { [key in TagSize]: string | undefined } = {
	["normal"]: undefined,
	["small"]: styles.root_small
};